import { useState } from "react";
import { Modal } from "semantic-ui-react";

import "components/CarNoteModal/CarNoteModal.scss";
import { DeleteCarNoteModal } from "components/CarNoteModal/components";
import { CarNoteModalForm } from "components/CarNoteModal/components/CarNoteModalForm";
import { useCreateCarNoteMutation, useUpdateCarNoteMutation } from "components/CarNoteModal/hooks";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { CAR_NOTE_TYPE } from "models";
import { toISOString } from "utils/dateHelperFunctions";

import type { Car, CarNote, CarNoteAttachment, DealerLocation } from "models";

export type CarNoteModalFormValues = Pick<CarNote, "note" | "visible_on_wo" | "visible_to_mechanic"> & {
  attachments: CarNoteAttachment[] | null;
};

type CarNoteModalProps = {
  car: Car;
  selectedNote: CarNote | null;
  location?: DealerLocation;
  onUnselectNote: () => void;
  onSetSelectedCarNote: (note: CarNote | null) => void;
};

const FormHelper = createFormHelper<CarNoteModalFormValues>();

export const CarNoteModal = ({ car, location, selectedNote, onUnselectNote, onSetSelectedCarNote }: CarNoteModalProps) => {
  const [hasValidationError, setHasValidationError] = useState(false);
  const [showDeleteCarNoteModal, setShowDeleteCarNoteModal] = useState(false);

  const { user } = usePermanentRealTimeUser();
  const { mutateAsync: createCarNote, isPending: isCreateCarNotePending } = useCreateCarNoteMutation();
  const { mutateAsync: updateCarNote, isPending: isUpdateCarNotePending } = useUpdateCarNoteMutation();

  const handleModalClose = () => {
    onSetSelectedCarNote(null);
    setHasValidationError(false);
  };

  const isFormInvalid = (values: CarNoteModalFormValues) => {
    setHasValidationError(!values.note);
    return !values.note;
  };

  const handleSubmit = async (values: CarNoteModalFormValues) => {
    if (isFormInvalid(values) || !user) return;
    const todayDate = toISOString(new Date());

    if (selectedNote)
      await updateCarNote({
        ...selectedNote,
        note: values.note,
        updated_on: todayDate,
        updated_by_id: user.id,
        visible_to_mechanic: values.visible_to_mechanic,
        visible_on_wo: values.visible_on_wo,
        attachments: values.attachments ?? []
      });
    else
      await createCarNote({
        id: -1,
        note: values.note,
        dms_nr: "",
        car_id: car.id,
        created_on: todayDate,
        updated_on: todayDate,
        attachments: values.attachments ?? [],
        created_by_id: user.id,
        updated_by_id: user.id,
        car_note_type_id: CAR_NOTE_TYPE.Default,
        visible_on_wo: values.visible_on_wo,
        visible_to_mechanic: values.visible_to_mechanic
      });

    handleModalClose();
  };

  return (
    <>
      <Modal open onClose={handleModalClose} closeOnDimmerClick={false}>
        <div className="CarNoteModal">
          <FormHelper.Form
            onSubmit={handleSubmit}
            defaultValues={{
              note: selectedNote?.note || "",
              attachments: selectedNote?.attachments || [],
              visible_on_wo: selectedNote?.visible_on_wo || false,
              visible_to_mechanic: selectedNote?.visible_to_mechanic || false
            }}
          >
            <CarNoteModalForm
              car={car}
              location={location}
              hasErrors={hasValidationError}
              isPending={isCreateCarNotePending || isUpdateCarNotePending}
              selectedNote={selectedNote}
              onClose={handleModalClose}
              onSubmit={handleSubmit}
              onUnselectNote={onUnselectNote}
              handleOpenDeleteCarNoteModal={() => setShowDeleteCarNoteModal(true)}
            />
          </FormHelper.Form>
        </div>
      </Modal>
      {selectedNote?.id && <DeleteCarNoteModal open={showDeleteCarNoteModal} id={selectedNote.id} onClose={handleModalClose} />}
    </>
  );
};
